import * as THREE from 'three';
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader';
import { BasketballDimensions } from '@shared/constants/BasketballDimensions';

export class Arena {
  private mesh: THREE.Group;
  private floorWidth: number;
  private floorLength: number;
  private rampHeight: number = 10; // Height of the ramps in meters
  private rampAngle: number = Math.PI / 6; // 30 degrees slope
  private rampDepth: number; // Will be calculated based on height and angle
  private chairModel: THREE.Group | null = null;

  constructor() {
    this.mesh = new THREE.Group();
    this.floorWidth = BasketballDimensions.floor.width;
    this.floorLength = BasketballDimensions.floor.length;
    this.rampDepth = this.rampHeight / Math.tan(this.rampAngle);
    this.loadChairModel().then(() => {
      this.createArena();
    });
  }

  private async loadChairModel(): Promise<void> {
    const loader = new GLTFLoader();
    try {
      const gltf = await loader.loadAsync('/assets/models/chair.glb');
      this.chairModel = gltf.scene;
      // Scale and rotate the chair model as needed
      this.chairModel.scale.set(4, 4, 4); // Adjust scale as needed
      this.chairModel.rotation.y = Math.PI / 2; // Rotate to face the court
      
      // Recursively set shadow properties on all meshes
      this.chairModel.traverse((node) => {
        if (node instanceof THREE.Mesh) {
          node.castShadow = true;
          node.receiveShadow = false;
        }
      });
      
    } catch (error) {
      console.error('Error loading chair model:', error);
    }
  }

  private createArena(): void {
    // Create ramps first (they're the main part of the arena)
    this.createRamps();
    
    // Create media table
    const mediaTable = this.createMediaTable();
    mediaTable.rotation.y = -Math.PI/2;
    mediaTable.position.set(this.floorWidth/2 - 1.5, 0, 0);
    this.mesh.add(mediaTable);
    
    // Create chairs instead of benches
    this.createChairs();
    this.createArenaFloor();
  }

  private createArenaFloor(): void {
    const floorGeometry = new THREE.PlaneGeometry(this.floorWidth*2, this.floorLength*2);
    const floorTexture = new THREE.TextureLoader().load('/images/oai_concrete_floor.png');
    floorTexture.wrapS = THREE.RepeatWrapping;
    floorTexture.wrapT = THREE.RepeatWrapping;
    floorTexture.repeat.set(8, 8); // Adjusted repeat to match the new dimensions
    
    const floorMaterial = new THREE.MeshStandardMaterial({
      map: floorTexture,
      roughness: 0.5,
      metalness: 0.1
    });
    
    const floor = new THREE.Mesh(floorGeometry, floorMaterial);
    floor.rotateX(-Math.PI/2);
    floor.position.set(0, -0.01, 0);
    floor.receiveShadow = true; // Enable receiving shadows on the floor
    this.mesh.add(floor);
  }

  private createChairs(): void {
    if (!this.chairModel) {
      console.error('Chair model not loaded');
      return;
    }

    const chairSpacing = 1.2; // Space between chairs in meters
    const numChairs = 8;
    const startZ = -(this.floorLength - chairSpacing) / 2;
    const xPosition = this.floorWidth/2 - 1.5;

    // Create chairs for team A (left side)
    for (let i = 0; i < numChairs; i++) {
      const chair = this.chairModel.clone();
      chair.position.set(xPosition, 0, startZ + i * chairSpacing);
      this.mesh.add(chair);
    }

    // Create chairs for team B (right side)
    for (let i = 0; i < numChairs; i++) {
      const chair = this.chairModel.clone();
      chair.position.set(xPosition, 0, -startZ - i * chairSpacing);      
      this.mesh.add(chair);
    }
  }

  private createMediaTable(): THREE.Group {
    // Media table dimensions
    const tableWidth = 14; // 12 meters wide
    const tableDepth = 1.5; // 1.5 meters deep
    const tableHeight = 1.2; // 1.2 meters high

    // Create table group to hold all parts
    const table = new THREE.Group();

    // Create table top
    const tableTopGeometry = new THREE.BoxGeometry(tableWidth, 0.1, tableDepth);
    const tableMaterial = new THREE.MeshStandardMaterial({ 
      color: 0x222222, // Dark grey color
      roughness: 0.8,
      metalness: 0.2
    });
    const tableTop = new THREE.Mesh(tableTopGeometry, tableMaterial);
    tableTop.position.y = tableHeight;

    // Create table front geometry
    const tableFrontGeometry = new THREE.PlaneGeometry(tableWidth, tableHeight);
    
    // Create temporary material for the front (will be updated when texture loads)
    const tableFrontMaterial = new THREE.MeshStandardMaterial({
      color: 0x222222,
      roughness: 0.1,
      metalness: 0.1
    });
    
    const tableFront = new THREE.Mesh(tableFrontGeometry, tableFrontMaterial);
    tableFront.position.z = tableDepth/2;
    tableFront.position.y = tableHeight/2;

    // Create sponsor logos quad
    const sponsorQuadGeometry = new THREE.PlaneGeometry(tableWidth, tableHeight);
    const sponsorQuadMaterial = new THREE.MeshStandardMaterial({
      color: 0xffffff,
      roughness: 0.4,
      metalness: 0.1,
      side: THREE.DoubleSide
    });
    const sponsorQuad = new THREE.Mesh(sponsorQuadGeometry, sponsorQuadMaterial);
    sponsorQuad.position.z = tableDepth/2 + 0.05; // Slightly in front of table front
    sponsorQuad.position.y = tableHeight/2;

    // Load texture
    const textureLoader = new THREE.TextureLoader();
    textureLoader.load('/images/sponsor_logos.jpeg', (texture) => {
      texture.wrapS = THREE.RepeatWrapping;
      texture.repeat.x = 3; // Tile the texture 3 times horizontally
      texture.minFilter = THREE.LinearFilter;
      texture.magFilter = THREE.LinearFilter;
      
      // Update the material with the new texture
      sponsorQuad.material = new THREE.MeshStandardMaterial({
        map: texture,
        color: 0xffffff,
        roughness: 0.4,
        metalness: 0.1,
        side: THREE.DoubleSide,
        transparent: true
      });
      sponsorQuad.material.needsUpdate = true;
    });

    // Add parts to the table group
    table.add(tableTop);
    table.add(tableFront);
    table.add(sponsorQuad);

    const tableGroup = new THREE.Group();
    tableGroup.add(table);

    return tableGroup;
  }

  private createRamp(params: {
    width: number,
    height: number,
    angle: number,
    textureRepeat: { x: number, y: number },
    texture: string
  }): THREE.Group {
    const { width, height, angle, textureRepeat, texture } = params;
    const rampDepth = height / Math.tan(angle);
    const rampLength = Math.sqrt(height * height + rampDepth * rampDepth);
    
    // Create the ramp geometry
    const rampGeometry = new THREE.PlaneGeometry(width, rampLength);
    const textureLoader = new THREE.TextureLoader();
    const crowdTexture = textureLoader.load(texture, 
      (texture) => {
        texture.colorSpace = THREE.SRGBColorSpace;
        texture.wrapS = THREE.RepeatWrapping;
        texture.wrapT = THREE.RepeatWrapping;
        texture.repeat.set(textureRepeat.x, textureRepeat.y);
        // Ensure crisp textures
        texture.minFilter = THREE.LinearFilter;
        texture.magFilter = THREE.LinearFilter;
      }
    );

    // Create material with better visual settings
    const rampMaterial = new THREE.MeshStandardMaterial({
        map: crowdTexture,
        roughness: 0.1, // Reduced roughness for more vibrancy
        metalness: 0.0, // Removed metalness to prevent washing out
        side: THREE.DoubleSide
    });
    
    // Create the ramp mesh
    const ramp = new THREE.Mesh(rampGeometry, rampMaterial);
    // Apply the ramp angle from floor
    ramp.rotation.x = Math.PI/2 - angle;
    ramp.position.set(0, height/2, rampDepth/2);
    
    // Create a group to hold the ramp
    const rampGroup = new THREE.Group();
    rampGroup.add(ramp);

    
    
    return rampGroup;
  }

  private createRamps(): void {
    // Right ramp (positive Z)
    const rightRamp = this.createRamp({
        width: this.floorWidth*2,
        height: this.rampHeight,
        angle: this.rampAngle,
        textureRepeat: { x: 6, y: 3 },
        texture: '/images/crowd_left2.png'
    });
    //rightRamp.rotation.y = Math.PI/4;
    //rightRamp.position.set(0, 0, this.floorLength/2 + this.floorWidth/2);
    rightRamp.position.set(0, 0, this.floorLength/2 + this.floorWidth/4);
    this.mesh.add(rightRamp);
    
    // Left ramp (negative Z)
    const leftRamp = this.createRamp({
        width: this.floorWidth*2,
        height: this.rampHeight,
        angle: this.rampAngle,
        textureRepeat: { x: -6, y: 3 },
        texture: '/images/crowd_left2.png'
    });
    //leftRamp.position.set(0, 0, -this.floorLength/2 - this.floorWidth/2);
    //leftRamp.rotation.y = Math.PI - Math.PI/4; // Rotate 180 degrees to face inward
    leftRamp.position.set(0, 0, -this.floorLength/2 - this.floorWidth/4);
    leftRamp.rotation.y = Math.PI; // Rotate 180 degrees to face inward
    this.mesh.add(leftRamp);
    
    // Back ramp (negative X)
    const backRamp = this.createRamp({
        width: this.floorLength*2,
        height: this.rampHeight,
        angle: this.rampAngle,
        textureRepeat: { x: 4, y: 1 },
        texture: '/images/crowd2.png'
    });
    backRamp.position.set(this.floorWidth/2, 0, 0);
    backRamp.rotation.y = Math.PI/2; // Rotate 90 degrees to face inward
    this.mesh.add(backRamp);
  }

  public getMesh(): THREE.Group {
    return this.mesh;
  }
} 